import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import { Check, SearchX } from "lucide-react";
import {
  Alert,
  Badge,
  Card,
  EmptyState,
  HoverPopover,
  SearchField,
  ToggleGroup,
} from "@/components/ui";
import { SaveBar } from "@/components/common";
import { useUnsavedFormGuard } from "@/hooks/useUnsavedFormGuard";
import { toastApiError } from "@/lib/errorMessages";
import { matchesTextQuery } from "@/lib/textSearch";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/store/authStore";
import { Permission } from "@/types";
import {
  StaffRolePreset,
  staffAccessKeys,
  staffAccessService,
  type StaffAccessProfile,
  type StaffRolePresetRead,
} from "@/services/staffAccessService";
import {
  ASSIGNABLE_STAFF_PERMISSIONS,
  SCOPE_PERMISSION_MAP,
  STAFF_PERMISSION_SCOPE_KEYS,
  missingPermissionRequirements,
  togglePermission,
  type StaffPermissionScopeKey,
} from "./staffPermissionScopes";

type RoleSelection = StaffRolePreset | "custom";

type StaffAccessDraft =
  | { mode: "preset"; key: StaffRolePreset }
  | { mode: "custom"; permissions: Set<Permission> };

const STAFF_ACCESS_FORM_ID = "staff-access-form";

function assignablePermissionSet(source: Iterable<string>): Set<Permission> {
  const assignable = new Set<string>(ASSIGNABLE_STAFF_PERMISSIONS);
  return new Set(
    [...source].filter((permission): permission is Permission =>
      assignable.has(permission),
    ),
  );
}

function samePermissionSet(
  left: ReadonlySet<Permission>,
  right: readonly Permission[],
): boolean {
  if (left.size !== right.length) return false;
  return right.every((permission) => left.has(permission));
}

function draftFromProfile(profile: StaffAccessProfile): StaffAccessDraft {
  if (profile.preset_key) {
    return { mode: "preset", key: profile.preset_key };
  }
  return { mode: "custom", permissions: new Set(profile.permissions) };
}

function isDraftDirty(
  draft: StaffAccessDraft,
  profile: StaffAccessProfile,
): boolean {
  if (draft.mode === "preset") {
    return draft.key !== profile.preset_key;
  }
  if (profile.preset_key !== null) return true;
  return !samePermissionSet(draft.permissions, profile.permissions);
}

function displayedPermissions(
  draft: StaffAccessDraft,
  presets: StaffRolePresetRead[],
  profile: StaffAccessProfile,
): ReadonlySet<Permission> {
  if (draft.mode === "custom") return draft.permissions;
  const preset = presets.find((item) => item.key === draft.key);
  return new Set(preset?.permissions ?? profile.permissions);
}

interface PermissionEntry {
  permission: Permission;
  action: string;
}

/** `readers:presets:create` → resource `readers:presets`, action `create`. */
function groupByResource(
  scopePermissions: readonly Permission[],
): { resource: string; entries: PermissionEntry[] }[] {
  const groups = new Map<string, PermissionEntry[]>();
  for (const permission of scopePermissions) {
    const separator = permission.lastIndexOf(":");
    const resource =
      separator === -1 ? permission : permission.slice(0, separator);
    const action =
      separator === -1 ? permission : permission.slice(separator + 1);
    const entries = groups.get(resource) ?? [];
    entries.push({ permission, action });
    groups.set(resource, entries);
  }
  return [...groups.entries()].map(([resource, entries]) => ({
    resource,
    entries,
  }));
}

interface PermissionToggleProps {
  permission: Permission;
  action: string;
  checked: boolean;
  locked: boolean;
  blocked: boolean;
  onChange: (enabled: boolean) => void;
}

/** Pill switch for one permission action inside the resource matrix. */
function PermissionToggle({
  permission,
  action,
  checked,
  locked,
  blocked,
  onChange,
}: PermissionToggleProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={permission}
      title={permission}
      disabled={locked || blocked}
      onClick={() => onChange(!checked)}
      className={cn(
        "inline-flex h-7 items-center gap-1.5 rounded-md px-2 font-mono text-xs ring-1 transition-all duration-150 ring-inset",
        "focus-visible:ring-primary-500 focus-visible:ring-2 focus-visible:outline-none",
        checked
          ? "bg-primary-50 text-primary-800 ring-primary-600/30 dark:text-primary-800"
          : "bg-surface-0 text-surface-600 ring-surface-200 hover:ring-surface-300 hover:text-surface-900",
        blocked && "cursor-not-allowed opacity-50",
        locked && "cursor-default",
      )}
    >
      <span
        className={cn(
          "flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-[4px] ring-1 ring-inset",
          checked
            ? "bg-primary-fill ring-primary-fill text-white"
            : "bg-surface-0 ring-surface-300",
        )}
        aria-hidden
      >
        {checked ? <Check className="h-2.5 w-2.5" strokeWidth={3} /> : null}
      </span>
      {action}
    </button>
  );
}

interface StaffAccessEditorProps {
  userId: string;
}

export function StaffAccessEditor({ userId }: StaffAccessEditorProps) {
  const { t } = useTranslation("adminUsers");
  const { t: tCommon } = useTranslation("common");
  const queryClient = useQueryClient();
  const getCurrentUserId = useAuthStore((s) => s.getCurrentUserId);
  const isSelf = getCurrentUserId() === userId;

  const {
    data: profile,
    isLoading,
    isError,
  } = useQuery({
    queryKey: staffAccessKeys.access(userId),
    queryFn: () => staffAccessService.getAccess(userId),
  });
  const { data: presets = [] } = useQuery({
    queryKey: staffAccessKeys.presets,
    queryFn: () => staffAccessService.listPresets(),
  });

  const [draft, setDraft] = useState<StaffAccessDraft | null>(null);
  const [permissionSearch, setPermissionSearch] = useState("");

  useEffect(() => {
    if (!profile) return;
    setDraft(draftFromProfile(profile));
  }, [profile]);

  const targetIsSuperAdmin = profile?.is_super_admin ?? false;
  const editingLocked = isSelf || targetIsSuperAdmin;

  const hasUnsavedChanges =
    !editingLocked && profile != null && draft != null
      ? isDraftDirty(draft, profile)
      : false;

  const { saveBarPhase, notifySaved } = useUnsavedFormGuard(hasUnsavedChanges);

  const handleRevert = () => {
    if (!profile) return;
    setDraft(draftFromProfile(profile));
  };

  const saveMutation = useMutation({
    mutationFn: () => {
      if (!draft) throw new Error("draft missing");
      if (draft.mode === "preset") {
        return staffAccessService.updateAccess(userId, {
          mode: "preset",
          preset_key: draft.key,
        });
      }
      return staffAccessService.updateAccess(userId, {
        mode: "custom",
        permissions: Array.from(draft.permissions),
      });
    },
    onSuccess: () => {
      toast.success(t("staffAccess.saveSuccess"));
      void queryClient.invalidateQueries({
        queryKey: staffAccessKeys.access(userId),
      });
      notifySaved();
    },
    onError: (err) => toastApiError(err, "generic", t("staffAccess.saveError")),
  });

  if (isLoading) {
    return (
      <p className="text-surface-500 text-sm">{t("staffAccess.loading")}</p>
    );
  }
  if (isError || !profile || !draft) {
    return (
      <p className="text-danger-600 text-sm">{t("staffAccess.loadError")}</p>
    );
  }

  const selectedRole: RoleSelection =
    draft.mode === "preset" ? draft.key : "custom";
  const isPresetBound = draft.mode === "preset";
  const permissions = displayedPermissions(draft, presets, profile);

  const applyRole = (selection: RoleSelection) => {
    if (editingLocked) return;
    if (selection === "custom") {
      setDraft({
        mode: "custom",
        permissions: assignablePermissionSet(permissions),
      });
      return;
    }
    if (!presets.some((item) => item.key === selection)) return;
    setDraft({ mode: "preset", key: selection });
  };

  const setPermission = (permission: Permission, enabled: boolean) => {
    if (editingLocked) return;
    setDraft({
      mode: "custom",
      permissions: togglePermission(permissions, permission, enabled),
    });
  };

  const roleOptions = [
    {
      value: "custom" as const,
      label: t("staffAccess.presetCustom"),
      disabled: editingLocked,
    },
    ...presets
      .filter(
        (role) =>
          role.key !== StaffRolePreset.SUPER_ADMIN || targetIsSuperAdmin,
      )
      .map((role) => ({
        value: role.key,
        label: t(`staffAccess.presets.${role.key}`),
        disabled: editingLocked,
      })),
  ];

  const filteredScopes: {
    scope: StaffPermissionScopeKey;
    permissions: readonly Permission[];
  }[] = [];
  for (const scope of STAFF_PERMISSION_SCOPE_KEYS) {
    const scopeLabel = t(`staffAccess.scopes.${scope}`);
    const matched = SCOPE_PERMISSION_MAP[scope].filter((permission) =>
      matchesTextQuery(permissionSearch, [permission, scopeLabel]),
    );
    if (matched.length > 0) {
      filteredScopes.push({ scope, permissions: matched });
    }
  }

  const enabledCount = ASSIGNABLE_STAFF_PERMISSIONS.filter((permission) =>
    permissions.has(permission),
  ).length;

  return (
    <div className="space-y-5 pb-28">
      <div className="border-surface-200 bg-surface-0 shadow-card animate-slide-up overflow-hidden rounded-xl border">
        <div className="flex flex-col gap-3 px-5 py-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
            <span className="text-surface-500 shrink-0 text-xs font-medium">
              {t("staffAccess.preset")}
            </span>
            <ToggleGroup
              variant="chips"
              size="sm"
              value={selectedRole}
              onChange={applyRole}
              options={roleOptions}
              ariaLabel={t("staffAccess.preset")}
            />
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Badge
              variant={isPresetBound ? "primary" : "accent"}
              size="lg"
            >
              {isPresetBound
                ? t(`staffAccess.presets.${draft.key}`)
                : t("staffAccess.presetCustom")}
            </Badge>
            <span className="text-surface-600 text-[13px] tabular-nums">
              {t("staffAccess.enabledSummary", {
                count: enabledCount,
                total: ASSIGNABLE_STAFF_PERMISSIONS.length,
              })}
            </span>
          </div>
        </div>
        <div className="border-surface-200 bg-surface-50/60 dark:bg-surface-100/30 flex flex-col gap-3 border-t px-5 py-3 lg:flex-row lg:items-center lg:justify-between">
          <p className="text-surface-500 min-w-0 text-xs">
            {isPresetBound
              ? t("staffAccess.permissionsHintPresetBound")
              : t("staffAccess.presetHint")}
          </p>
          <SearchField
            searchLabel={tCommon("search")}
            clearSearchLabel={tCommon("clearSearch")}
            value={permissionSearch}
            onChange={setPermissionSearch}
            placeholder={t("staffAccess.searchPermissions")}
            className="w-full shrink-0 lg:w-72"
          />
        </div>
      </div>

      {isSelf ? (
        <Alert
          variant="warning"
          title={t("staffAccess.cannotEditSelfTitle")}
        >
          {t("staffAccess.cannotEditSelf")}
        </Alert>
      ) : null}

      {targetIsSuperAdmin && !isSelf ? (
        <Alert
          variant="info"
          title={t("staffAccess.immutableSuperTitle")}
        >
          {t("staffAccess.immutableSuper")}
        </Alert>
      ) : null}

      <form
        id={STAFF_ACCESS_FORM_ID}
        onSubmit={(event) => {
          event.preventDefault();
          if (editingLocked || !hasUnsavedChanges) return;
          saveMutation.mutate();
        }}
      >
        {filteredScopes.length === 0 ? (
          <EmptyState
            icon={SearchX}
            title={t("staffAccess.searchEmpty")}
            className="border-surface-200 rounded-xl border border-dashed"
          />
        ) : (
          <div className="gap-4 xl:columns-2">
            {filteredScopes.map(({ scope, permissions: scopePermissions }) => {
              const scopeEnabled = scopePermissions.filter((permission) =>
                permissions.has(permission),
              ).length;
              return (
                <Card
                  key={scope}
                  className="mb-4 break-inside-avoid overflow-hidden"
                >
                  <div className="border-surface-200 flex items-center justify-between gap-3 border-b px-4 py-2.5">
                    <h3 className="text-surface-900 text-[13px] font-semibold">
                      {t(`staffAccess.scopes.${scope}`)}
                    </h3>
                    <span
                      className={cn(
                        "rounded-md px-1.5 py-0.5 text-xs font-medium tabular-nums",
                        scopeEnabled === scopePermissions.length
                          ? "bg-primary-50 text-primary-700"
                          : scopeEnabled === 0
                            ? "bg-surface-100 text-surface-500"
                            : "bg-warning-50 text-warning-700",
                      )}
                    >
                      {scopeEnabled}/{scopePermissions.length}
                    </span>
                  </div>
                  <ul className="divide-surface-100 divide-y">
                    {groupByResource(scopePermissions).map(
                      ({ resource, entries }) => (
                        <li
                          key={resource}
                          className="flex flex-col gap-1.5 px-4 py-2.5 sm:flex-row sm:items-start sm:gap-3"
                        >
                          <span className="text-surface-500 w-36 shrink-0 pt-1 font-mono text-xs break-all">
                            {resource}
                          </span>
                          <div className="flex min-w-0 flex-wrap gap-1.5">
                            {entries.map(({ permission, action }) => {
                              const missingRequirements =
                                missingPermissionRequirements(
                                  permission,
                                  permissions,
                                );
                              const isOn = permissions.has(permission);
                              const blockedByRequirements =
                                missingRequirements.length > 0 && !isOn;
                              // Block enabling without prerequisites; still allow turning orphans off.
                              const toggle = (
                                <PermissionToggle
                                  permission={permission}
                                  action={action}
                                  checked={isOn}
                                  locked={editingLocked}
                                  blocked={blockedByRequirements}
                                  onChange={(enabled) =>
                                    setPermission(permission, enabled)
                                  }
                                />
                              );
                              if (!blockedByRequirements || editingLocked) {
                                return (
                                  <span key={permission}>{toggle}</span>
                                );
                              }
                              return (
                                <HoverPopover
                                  key={permission}
                                  placement="above"
                                  contentClassName="max-w-sm space-y-1.5 font-normal"
                                  content={
                                    <div className="space-y-1.5">
                                      <p>
                                        {t("staffAccess.requiresPermissions")}
                                      </p>
                                      <ul className="text-surface-0/90 space-y-0.5 font-mono text-[11px] font-normal">
                                        {missingRequirements.map((required) => (
                                          <li key={required}>{required}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  }
                                >
                                  <span className="inline-flex">{toggle}</span>
                                </HoverPopover>
                              );
                            })}
                          </div>
                        </li>
                      ),
                    )}
                  </ul>
                </Card>
              );
            })}
          </div>
        )}
      </form>

      {!editingLocked ? (
        <SaveBar
          phase={saveBarPhase}
          isPending={saveMutation.isPending}
          formId={STAFF_ACCESS_FORM_ID}
          onRevert={handleRevert}
        />
      ) : null}
    </div>
  );
}
